import { HttpErrorResponse } from "@angular/common/http";
import { EvidenceFileActionsService } from "../../services/evidence_file_actions.service";
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { Router, UrlTree } from "@angular/router";
import { NzModalService } from "ng-zorro-antd/modal";
import { auditTime, filter, map, merge, Observable, Subject, Subscription, debounceTime } from "rxjs";
import {
    AssessmentTaskLaunchService, AssessmentTaskLaunchState
} from "../../services/assessment_task_launch.service";
import { AssessmentTaskFormLaunchService } from "../../services/assessment_task_form_launch.service";
import { AssessmentTasksService } from "../../services/assessment_tasks.service";
import { AssessmentWorkItemsService } from "../../services/assessment_work_items.service";
import { isWorkItemStatusSelector } from "../../services/assessment_work_items.dto";
import type {
    AssessmentWorkItem, AssessmentWorkItemEvidenceSummary, AssessmentWorkItemOptionFilters,
    AssessmentWorkItemStatusSelector, AssessmentWorkItemType
} from "../../services/assessment_work_items.types";
import { workItemIdentity, workItemStatusPresentation } from "../../services/assessment_work_items.types";
import { AssessmentTaskInvalidationService } from "../../services/assessment_task_invalidation.service";
import { EvidenceRequirementInvalidationService } from "../../services/evidence_requirement_invalidation.service";
import { EvidenceRequirementsLaunchService } from "../../services/evidence_requirements_launch.service";
import { LoginService } from "../../services/login.service";
import { ProductLicensesService } from "../../services/product_licenses.service";
import type {
    AssessmentTaskAssessmentOption, AssessmentTaskAssigneeOption,
    AssessmentTaskPage, AssessmentTaskRequirementRef, AssessmentTaskStatus,
    AssessmentTaskStatusOption, AssessmentTaskSummary, AssessmentTaskSort
} from "../../services/assessment_tasks.types";
import { encodeBase64RouteValue } from "../../utils/base64_route_value.util";
import { assessmentTaskDueState } from "../../utils/assessment_task_date.util";
import { evidenceRequirementDueState } from "../../utils/evidence_requirement_due_date.util";
import { isAssessmentTaskStatus, assessmentTaskStatusPresentation } from "../../utils/assessment_task_status.util";
import { formatDateTimeDisplay } from "../../utils/date_time_display.util";

interface FilterChoice<T extends number | string> {
    value: T;
    label: string;
}

interface FilterOptionState<T extends number | string> {
    items: FilterChoice<T>[];
    labels: Map<T, string>;
    page: number;
    total: number;
    search: string;
    loading: boolean;
    error: string | null;
    request: Subscription | null;
    version: number;
}

interface TaskActionError {
    message: string;
    conflict: boolean;
}

const OPTION_PAGE_SIZE = 25;
const EMPTY_IDS: number[] = [];
const EMPTY_STATUSES: (AssessmentTaskStatus | AssessmentWorkItemStatusSelector)[] = [];

function sameOrderedValues<T>(left: readonly T[], right: readonly T[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}

function newOptionState<T extends number | string>(): FilterOptionState<T> {
    return {
        items: [], labels: new Map<T, string>(), page: 0, total: 0, search: "",
        loading: false, error: null, request: null, version: 0
    };
}

function normalizeSearch(value: unknown, maxLength: number): string {
    return (typeof value === "string" ? value : "")
        .slice(0, maxLength).replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, "");
}

function uniqueIds(values: unknown): number[] {
    if (!Array.isArray(values)) return [];
    return [...new Set(values.filter((id): id is number =>
        typeof id === "number" && Number.isSafeInteger(id) && id > 0))].slice(0, 100);
}

@Component({
    selector: "app-assessment-task-browser-modal",
    templateUrl: "./assessment_task_browser_modal.component.html",
    styleUrls: ["./assessment_task_browser_modal.component.scss"]
})
export class AssessmentTaskBrowserModalComponent implements OnInit, OnDestroy {
    state: AssessmentTaskLaunchState;
    searchText = "";
    filtersVisible = false;
    readonly sortOptions: { value: AssessmentTaskSort; label: string; icon: string; accessibleLabel: string }[] = [
        { value: "due_date_desc", label: "Due Date", icon: "arrow-down", accessibleLabel: "Due Date descending" },
        { value: "due_date_asc", label: "Due Date", icon: "arrow-up", accessibleLabel: "Due Date ascending" },
        { value: "created_at_desc", label: "Created At", icon: "arrow-down", accessibleLabel: "Created At descending" },
        { value: "created_at_asc", label: "Created At", icon: "arrow-up", accessibleLabel: "Created At ascending" },
        { value: "updated_at_desc", label: "Updated At", icon: "arrow-down", accessibleLabel: "Updated At descending" },
        { value: "updated_at_asc", label: "Updated At", icon: "arrow-up", accessibleLabel: "Updated At ascending" }
    ];
    readonly assigneeOptions = newOptionState<number>();
    readonly assessmentOptions = newOptionState<number>();
    readonly statusOptions = newOptionState<AssessmentTaskStatus | AssessmentWorkItemStatusSelector>();
    readonly statusPresentation = assessmentTaskStatusPresentation;
    readonly workItemStatusPresentation = workItemStatusPresentation;
    readonly dueState = assessmentTaskDueState;
    readonly evidenceDueState = evidenceRequirementDueState;
    readonly formatDateTime = formatDateTimeDisplay;
    readonly bodyStyle = { "max-height": "none", "overflow-y": "auto" };
    readonly commentPending = new Set<number | string>();
    readonly deleteErrors = new Map<number, TaskActionError>();
    readonly deleteRequests = new Map<number, Subscription>();
    readonly requirementLinks = new Map<string, UrlTree>();
    readonly assessmentLinks = new Map<number, UrlTree>();
    navigationError: string | null = null;
    hasLoadedInitialPage = false;
    selectedAssigneeIds: number[] = [];
    readonly emptyIds = EMPTY_IDS;
    readonly emptyStatuses = EMPTY_STATUSES;

    private readonly subscriptions = new Subscription();
    private readonly textSearch = new Subject<{ value: string; scope: number; token: string | null }>();
    private readonly assigneeSearch = new Subject<{ value: string; scope: number; token: string | null }>();
    private readonly assessmentSearch = new Subject<{ value: string; scope: number; token: string | null }>();
    private scopeVersion = 0;
    private openContext: AssessmentTaskLaunchState["context"] = null;
    private licenseRequest: Subscription | null = null;
    private licenseRequestVersion = 0;
    private readonly navigableLicenses = new Map<number, "roc_form" | "saq_form">();
    private readonly taskCreationLicenses = new Set<number>();

    constructor(
        private readonly launch: AssessmentTaskLaunchService,
        private readonly formLaunch: AssessmentTaskFormLaunchService,
        private readonly tasks: AssessmentTasksService,
        private readonly workItems: AssessmentWorkItemsService,
        private readonly taskInvalidations: AssessmentTaskInvalidationService,
        private readonly evidenceInvalidations: EvidenceRequirementInvalidationService,
        private readonly evidenceLaunch: EvidenceRequirementsLaunchService,
        private readonly productLicenses: ProductLicensesService,
        private readonly login: LoginService,
        private readonly router: Router,
        private readonly modal: NzModalService,
        private readonly cdr: ChangeDetectorRef,
        private readonly evidenceFileActions: EvidenceFileActionsService
    ) {
        // Let the initial state emission initialize requests if a route opened the modal first.
        this.state = { ...launch.getSnapshot(), isOpen: false, context: null };
    }

    @HostListener("window:focus")
    onWindowFocus(): void {
        if (this.state.isOpen) this.cdr.markForCheck();
    }

    ngOnInit(): void {
        this.subscriptions.add(this.textSearch.pipe(debounceTime(300)).subscribe(({ value, scope, token }) => {
            if (scope === this.scopeVersion && token === this.login.getToken() &&
                this.state.isOpen && value !== (this.activeFilters.search || "")) {
                if (this.isCombinedHeader) this.launch.setHeaderFilters({ search: value || undefined });
                else this.launch.setFilters({ search: value || undefined });
            }
        }));
        this.subscriptions.add(this.assigneeSearch.pipe(debounceTime(250)).subscribe(({ value, scope, token }) => {
            if (scope === this.scopeVersion && token === this.login.getToken() &&
                this.state.isOpen && value === this.assigneeOptions.search) {
                this.loadAssignees();
            }
        }));
        this.subscriptions.add(this.assessmentSearch.pipe(debounceTime(250)).subscribe(({ value, scope, token }) => {
            if (scope === this.scopeVersion && token === this.login.getToken() &&
                this.state.isOpen && value === this.assessmentOptions.search) {
                this.loadAssessments();
            }
        }));
        this.subscriptions.add(this.launch.watchState().subscribe((state) => {
            const newContext = state.isOpen && (!this.state.isOpen || state.context !== this.openContext);
            const closed = !state.isOpen && this.state.isOpen;
            const pageChanged = state.context?.kind === "header"
                ? state.combinedHeader?.items !== this.state.combinedHeader?.items
                : state.items !== this.state.items;
            const filtersChanged = (state.context?.kind === "header"
                ? state.combinedHeader?.filters !== this.state.combinedHeader?.filters
                : state.filters !== this.state.filters) && !pageChanged;
            this.state = state;
            const explicitIds = this.activeFilters.assigneeUserIds || EMPTY_IDS;
            const currentUserId = state.isOpen && state.context?.kind === "header" &&
                this.activeFilters.assignedToMe && !explicitIds.length ? this.login.getCurrentUserId() : null;
            const nextSelectedIds = currentUserId ? [currentUserId] : explicitIds;
            if (!sameOrderedValues(this.selectedAssigneeIds, nextSelectedIds)) {
                this.selectedAssigneeIds = [...nextSelectedIds];
            }
            if (newContext || closed) {
                this.hasLoadedInitialPage = false;
                this.filtersVisible = false;
                this.resetOptionScope();
                this.clearDeleteRequests();
                this.openContext = state.isOpen ? state.context : null;
                if (state.isOpen) {
                    this.searchText = this.activeFilters.search || "";
                }
            }
            if (filtersChanged && this.isCombinedHeader) {
                this.invalidateHeaderOptions();
                if (this.hasLoadedInitialPage) {
                    this.loadAssessments();
                    this.loadStatuses();
                }
            }
            if (state.isOpen && !this.activeLoading && !this.activeError && !this.hasLoadedInitialPage) {
                this.hasLoadedInitialPage = true;
                if (!state.filters.taskId) {
                    if (state.context?.kind !== "header") this.loadAssignees();
                    if (state.context?.kind !== "assessment") this.loadAssessments();
                    this.loadStatuses();
                }
                this.loadNavigableLicenses();
            }
            if (newContext || closed || filtersChanged || this.activeError) this.commentPending.clear();
            if (pageChanged) {
                this.deleteErrors.clear();
                this.buildRequirementLinks();

            }
        }));
        this.subscriptions.add(this.taskInvalidations.watchInvalidations().pipe(
            filter((event) => {
                if (event.kind !== "scope" || !this.state.isOpen) return false;
                const assessmentIds = this.activeFilters.assessmentIds;
                return event.productLicenseId === null || !assessmentIds?.length ||
                    assessmentIds.includes(event.productLicenseId);
            }),
            map(() => ({ scope: this.scopeVersion, token: this.login.getToken(), context: this.state.context })),
            auditTime(0)
        ).subscribe(({ scope, token, context }) => {
            if (scope === this.scopeVersion && token === this.login.getToken() &&
                context === this.state.context && this.state.isOpen) {
                this.loadNavigableLicenses();
            }
        }));
        this.subscriptions.add(merge(
            this.taskInvalidations.watchInvalidations().pipe(map((event) => ({ source: "task" as const, event }))),
            this.evidenceInvalidations.watchChanges().pipe(map((event) => ({ source: "evidence" as const, event })))
        ).pipe(
            filter((hint) => {
                if (!this.isCombinedHeader) return false;
                const filters = this.state.combinedHeader!.filters;
                if (hint.source === "evidence") {
                    if (hint.event.accountId !== this.login.getCurrentAccountId() ||
                        filters.itemType === "task") return false;
                } else if (filters.itemType === "evidence" && hint.event.kind !== "scope") return false;
                return hint.event.productLicenseId === null || !filters.assessmentIds?.length ||
                    filters.assessmentIds.includes(hint.event.productLicenseId);
            }),
            map(() => ({
                scope: this.scopeVersion,
                context: this.state.context,
                filters: this.state.combinedHeader?.filters,
                token: this.login.getToken()
            })),
            auditTime(0)
        ).subscribe(({ scope, context, filters, token }) => {
            if (scope !== this.scopeVersion || context !== this.state.context ||
                filters !== this.state.combinedHeader?.filters || token !== this.login.getToken() ||
                !this.isCombinedHeader || !this.hasLoadedInitialPage) return;
            this.invalidateHeaderOptions();
            this.loadAssessments();
            this.loadStatuses();
        }));
    }

    ngOnDestroy(): void {
        this.resetOptionScope();
        this.clearDeleteRequests();
        this.commentPending.clear();
        this.subscriptions.unsubscribe();
    }

    get isCombinedHeader(): boolean {
        return this.state.context?.kind === "header" && this.state.combinedHeader !== null;
    }

    get canOfferTaskCreation(): boolean {
        if (!this.state.isOpen || !this.login.isLoggedIn() || this.login.isFileUploader()) return false;
        const context = this.state.context;
        return context?.kind === "header" ? this.taskCreationLicenses.size > 0
            : !!context && this.taskCreationLicenses.has(context.productLicenseId);
    }

    get activeFilters(): AssessmentTaskLaunchState["filters"] | NonNullable<AssessmentTaskLaunchState["combinedHeader"]>["filters"] {
        return this.isCombinedHeader ? this.state.combinedHeader!.filters : this.state.filters;
    }

    get activeLoading(): boolean {
        return this.isCombinedHeader ? this.state.combinedHeader!.isLoading : this.state.isLoading;
    }

    get activeError(): string | null {
        return this.isCombinedHeader ? this.state.combinedHeader!.error : this.state.error;
    }

    get activeTotal(): number {
        return this.isCombinedHeader ? this.state.combinedHeader!.total : this.state.total;
    }

    get taskRows(): AssessmentTaskSummary[] {
        return this.isCombinedHeader
            ? this.state.combinedHeader!.items.flatMap((item) => item.kind === "task" ? [item.task] : [])
            : this.state.items;
    }

    get visibleRows(): AssessmentWorkItem[] {
        return this.isCombinedHeader
            ? this.state.combinedHeader!.items
            : this.state.items.map((task) => ({ kind: "task" as const, task }));
    }

    get evidenceRows(): Extract<AssessmentWorkItem, { kind: "evidence" }>[] {
        return this.isCombinedHeader
            ? this.state.combinedHeader!.items.filter((item): item is Extract<AssessmentWorkItem,
                { kind: "evidence" }> => item.kind === "evidence")
            : [];
    }

    get activeItemCount(): number {
        return this.isCombinedHeader ? this.state.combinedHeader!.items.length : this.state.items.length;
    }

    get displayedAssignees(): FilterChoice<number>[] {
        const selected = this.selectedAssigneeIds;
        const items = this.withSelected(this.assigneeOptions, selected, "User");
        const currentUserId = this.login.getCurrentUserId();
        if (this.state.context?.kind === "header" && currentUserId && selected.includes(currentUserId)) {
            const index = items.findIndex((item) => item.value === currentUserId);
            if (index >= 0) items[index] = {
                ...items[index], label: this.login.getCurrentUserDisplayName() || items[index].label
            };
        }
        return items;
    }

    get displayedAssessments(): FilterChoice<number>[] {
        return this.withSelected(this.assessmentOptions, this.activeFilters.assessmentIds || EMPTY_IDS, "Assessment")
            .map((item) => ({ ...item, label: this.assessmentOptions.labels.get(item.value) ||
                this.taskRows.find((task) => task.productLicenseId === item.value)?.assessmentLabel ||
                this.evidenceRows.find((row) => row.evidence.productLicenseId === item.value)?.evidence.assessmentLabel ||
                item.label }));
    }

    get displayedStatuses(): FilterChoice<AssessmentTaskStatus | AssessmentWorkItemStatusSelector>[] {
        return this.withSelected(this.statusOptions, this.activeFilters.statuses || EMPTY_STATUSES, "Status");
    }

    get emptyMessage(): string {
        if (this.isCombinedHeader) return "No assigned work found.";
        return this.state.filters.taskId
            ? "This task is unavailable in your current scope."
            : "No tasks found.";
    }

    get modalTitle(): string {
        if (this.state.filters.taskId) return "Task Details";
        switch (this.state.context?.kind) {
            case "header": return "Tasks Assigned to You";
            case "assessment": return "This Assessment's Assigned Tasks";
            default: return "Assigned Tasks";
        }
    }

    close(): void {
        this.launch.close();
    }

    get isEvidenceModalOpen(): boolean {
        return this.evidenceLaunch.getSnapshot().isOpen;
    }

    openEvidence(evidence: AssessmentWorkItemEvidenceSummary, action?: "edit" | "delete" | "email"): void {
        if (!this.isCombinedHeader || !this.state.isOpen || this.activeLoading || this.activeError) return;
        if (this.taskWritePending(this.evidenceCommentTarget(evidence).id)) return;
        const current = this.state.combinedHeader!.items.find((item) => item.kind === "evidence" &&
            item.evidence.id === evidence.id &&
            item.evidence.productLicenseId === evidence.productLicenseId);
        if (!current || current.kind !== "evidence") return;
        if (action === "edit" && !current.evidence.capabilities.canEditEvidence ||
            action === "delete" && !current.evidence.capabilities.canDeleteEvidence ||
            action === "email" && !current.evidence.capabilities.canEmailEvidence) return;
        // Keep the browser open beneath the exact-item modal so its filters and page survive.
        this.evidenceLaunch.openItem(current.evidence.productLicenseId, current.evidence.id, current.evidence.merchantId, action);
    }

    showFilters(): void {
        this.filtersVisible = true;
    }

    openEvidenceFileAction(evidence: AssessmentWorkItemEvidenceSummary, action: "upload" | "download" | "preview" | "manage"): void {
        const scope = this.scopeVersion;
        const current = () => this.isCombinedHeader && this.state.isOpen && scope === this.scopeVersion &&
            !this.activeLoading && !this.activeError && !this.taskWritePending(this.evidenceCommentTarget(evidence).id) &&
            this.state.combinedHeader!.items.some(item => item.kind === "evidence" &&
                item.evidence.id === evidence.id && item.evidence.productLicenseId === evidence.productLicenseId &&
                item.evidence.merchantId === evidence.merchantId &&
                (action === "upload" ? item.evidence.capabilities.canUpload :
                    item.evidence.linkedFileAccessible && item.evidence.linkedSecureStorageFileId === evidence.linkedSecureStorageFileId));
        if (!current()) return;
        const context = { isCurrent: current, merchantId: evidence.merchantId };
        this.subscriptions.add(action === "upload"
            ? this.evidenceFileActions.openUpload({ productLicenseId: evidence.productLicenseId, evidenceRequirementId: evidence.id }, context)
            : this.evidenceFileActions.openLinkedFile(evidence.productLicenseId, {
                evidenceRequirementId: evidence.id, secureStorageFileId: evidence.linkedSecureStorageFileId!, action
            }, context));
    }

    retry(): void {
        this.launch.refresh();
    }

    addTask(): void {
        const context = this.state.isOpen ? this.state.context : null;
        if (!context || !this.canOfferTaskCreation) return;
        switch (context.kind) {
            case "requirement":
                this.formLaunch.openRequirementCreate(context.productLicenseId, context.requirement);
                break;
            case "assessment":
            case "task":
                this.formLaunch.openAssessmentCreate(context.productLicenseId);
                break;
            case "header":
                this.formLaunch.openHeaderCreate();
                break;
        }
        this.launch.close();
    }

    editTask(task: AssessmentTaskSummary): void {
        if (!this.state.isOpen || !(task.capabilities.canEditTask || task.capabilities.canChangeStatus) ||
            !this.taskRows.some((item) => item.id === task.id) ||
            this.taskWritePending(task.id)) return;
        this.formLaunch.openCreatorEdit(task.productLicenseId, task.id);
        this.launch.close();
    }

    taskWritePending(taskId: number | string): boolean {
        return (typeof taskId === "number" && this.deleteRequests.has(taskId)) || this.commentPending.has(taskId);
    }

    reloadStatusTask(): void {
        if (this.state.isOpen) this.launch.refresh();
    }

    confirmDeleteTask(task: AssessmentTaskSummary): void {
        const current = this.taskRows.find((item) => item.id === task.id);
        if (!this.state.isOpen || this.activeLoading || !current?.capabilities.canDeleteTask ||
            this.taskWritePending(task.id) || this.deleteErrors.get(task.id)?.conflict) return;
        const revision = current.revision;
        this.modal.confirm({
            nzTitle: "Delete task?",
            nzContent: "This task will be deleted",
            nzOkText: "Delete task",
            nzOkDanger: true,
            nzOnOk: () => {
                const latest = this.taskRows.find((item) => item.id === task.id);
                if (!this.state.isOpen || this.activeLoading ||
                    !latest?.capabilities.canDeleteTask || this.taskWritePending(task.id)) return;
                if (latest.revision !== revision) {
                    this.deleteErrors.set(task.id, {
                        conflict: true,
                        message: "This task changed. Reload the list before deleting it."
                    });
                    return;
                }
                this.deleteTask(latest);
            }
        });
    }

    changePage(page: number): void {
        if (page !== this.activeFilters.page && !this.activeLoading) this.launch.setPage(page);
    }

    removeTaskSelector(): void {
        this.launch.setFilters({ taskId: undefined });
    }

    removeRequirementSelector(): void {
        this.launch.setFilters({ requirement: undefined });
    }

    requirementLinkKey(task: AssessmentTaskSummary, requirement: AssessmentTaskRequirementRef): string {
        return JSON.stringify([task.id, requirement.sectionKey, requirement.requirementKey]);
    }

    evidenceCommentTarget(evidence: AssessmentWorkItemEvidenceSummary) {
        return { id: `evidence:${evidence.productLicenseId}:${evidence.id}`, capabilities: { canAddComment: true } };
    }

    deleteRequestsPending(taskId: number): boolean { return this.deleteRequests.has(taskId); }
    setCommentPending(key: number | string, pending: boolean): void {
        if (pending) this.commentPending.add(key); else this.commentPending.delete(key);
    }

    retryNavigation(): void {
        if (this.state.isOpen) this.loadNavigableLicenses();
    }

    changeTextSearch(value: string): void {
        this.searchText = normalizeSearch(value, 200);
        this.textSearch.next({ value: this.searchText, scope: this.scopeVersion, token: this.login.getToken() });
    }

    changeSort(value: AssessmentTaskSort): void {
        if (this.isCombinedHeader) return;
        if (value !== this.state.filters.sort && this.sortOptions.some((option) => option.value === value)) {
            this.launch.setFilters({ sort: value });
        }
    }

    changeItemType(value: AssessmentWorkItemType): void {
        if (!this.isCombinedHeader ||
            (value !== "all" && value !== "task" && value !== "evidence") ||
            value === this.state.combinedHeader?.filters.itemType) return;
        this.launch.setHeaderFilters({ itemType: value });
    }

    changeAssignees(values: unknown): void {
        const assigneeUserIds = uniqueIds(values);
        if (sameOrderedValues(assigneeUserIds, this.selectedAssigneeIds)) return;
        if (this.isCombinedHeader) this.launch.setHeaderFilters({ assigneeUserIds, assignedToMe: false });
        else this.launch.setFilters({ assigneeUserIds, assignedToMe: false });
    }

    changeAssessments(values: unknown): void {
        const assessmentIds = uniqueIds(values);
        if (sameOrderedValues(assessmentIds, this.activeFilters.assessmentIds || EMPTY_IDS)) return;
        if (this.isCombinedHeader) this.launch.setHeaderFilters({ assessmentIds });
        else this.launch.setFilters({ assessmentIds });
    }

    changeStatuses(values: unknown): void {
        if (this.isCombinedHeader) {
            const statuses = Array.isArray(values)
                ? [...new Set(values.filter(isWorkItemStatusSelector))] : [];
            if (!sameOrderedValues(statuses, this.activeFilters.statuses || EMPTY_STATUSES)) {
                this.launch.setHeaderFilters({ statuses });
            }
            return;
        }
        const statuses = Array.isArray(values)
            ? [...new Set(values.filter(isAssessmentTaskStatus))] : [];
        if (!sameOrderedValues(statuses, this.state.filters.statuses || EMPTY_STATUSES)) {
            this.launch.setFilters({ statuses });
        }
    }

    searchAssignees(value: string): void {
        const search = normalizeSearch(value, 100);
        if (search === this.assigneeOptions.search) return;
        this.prepareOptionSearch(this.assigneeOptions, search);
        this.assigneeSearch.next({
            value: this.assigneeOptions.search, scope: this.scopeVersion, token: this.login.getToken()
        });
    }

    searchAssessments(value: string): void {
        const search = normalizeSearch(value, 100);
        if (search === this.assessmentOptions.search) return;
        this.prepareOptionSearch(this.assessmentOptions, search);
        this.assessmentSearch.next({
            value: this.assessmentOptions.search, scope: this.scopeVersion, token: this.login.getToken()
        });
    }

    openAssignees(open: boolean): void {
        if (open) this.loadAssignees();
    }

    openAssessments(open: boolean): void {
        if (open) this.loadAssessments();
    }

    openStatuses(open: boolean): void {
        if (open) this.loadStatuses();
    }

    moreAssignees(): void {
        if (!this.assigneeOptions.loading &&
            this.assigneeOptions.page * OPTION_PAGE_SIZE < this.assigneeOptions.total) {
            this.loadAssignees(true);
        }
    }

    moreAssessments(): void {
        if (!this.assessmentOptions.loading &&
            this.assessmentOptions.page * OPTION_PAGE_SIZE < this.assessmentOptions.total) {
            this.loadAssessments(true);
        }
    }

    trackWorkItem(_: number, item: AssessmentWorkItem): string {
        return workItemIdentity(item);
    }

    trackChoice<T extends number | string>(_: number, choice: FilterChoice<T>): T {
        return choice.value;
    }

    private withSelected<T extends number | string>(
        options: FilterOptionState<T>, selected: readonly T[], fallback: string
    ): FilterChoice<T>[] {
        const items = [...options.items];
        const loaded = new Set(items.map((item) => item.value));
        for (const value of selected) {
            if (!loaded.has(value)) items.push({ value, label: options.labels.get(value) || `${fallback} #${value}` });
        }
        return items;
    }

    private resetOptionScope(): void {
        this.scopeVersion += 1;
        this.licenseRequest?.unsubscribe();
        this.licenseRequest = null;
        this.licenseRequestVersion += 1;
        this.navigationError = null;
        this.navigableLicenses.clear();
        this.taskCreationLicenses.clear();
        this.requirementLinks.clear();
        this.assessmentLinks.clear();
        for (const options of [this.assigneeOptions, this.assessmentOptions, this.statusOptions]) {
            options.request?.unsubscribe();
            options.request = null;
            options.version += 1;
            options.items = [];
            options.labels.clear();
            options.page = 0;
            options.total = 0;
            options.search = "";
            options.loading = false;
            options.error = null;
        }
        this.searchText = "";
    }

    private invalidateHeaderOptions(): void {
        // Keep selected labels, but never publish choices from the previous
        // combined filter snapshot after a header filter changes.
        for (const options of [this.assigneeOptions, this.assessmentOptions, this.statusOptions]) {
            options.request?.unsubscribe();
            options.request = null;
            options.version += 1;
            options.items = [];
            options.page = 0;
            options.total = 0;
            options.loading = false;
            options.error = null;
        }
    }

    private loadNavigableLicenses(): void {
        this.licenseRequest?.unsubscribe();
        const version = ++this.licenseRequestVersion;
        const scope = this.scopeVersion;
        const context = this.state.context;
        const token = this.login.getToken();
        const isCurrent = (): boolean => scope === this.scopeVersion &&
            version === this.licenseRequestVersion && context === this.state.context &&
            token === this.login.getToken() && this.state.isOpen;
        this.navigationError = null;
        this.navigableLicenses.clear();
        this.taskCreationLicenses.clear();
        this.requirementLinks.clear();
        this.assessmentLinks.clear();
        // This fresh task-only read does not update the shared license stream or its compliance subscribers.
        const request = this.productLicenses.loadTaskAssessmentChoices().subscribe({
            next: (licenses) => {
                if (!isCurrent()) return;
                this.navigableLicenses.clear();
                for (const license of licenses) {
                    if (license.canCreateTask === true) this.taskCreationLicenses.add(license.id);
                    const name = license.displayName?.trim();
                    if (name) this.assessmentOptions.labels.set(license.id, name);
                    if (license.formKey === "roc_form" || license.formKey === "saq_form") {
                        this.navigableLicenses.set(license.id, license.formKey);
                    }
                }
                this.buildRequirementLinks();
            },
            error: () => {
                if (!isCurrent()) return;
                this.navigationError = "Unable to load assessment links.";
                this.licenseRequest = null;
            },
            complete: () => {
                if (isCurrent()) {
                    this.licenseRequest = null;
                }
            }
        });
        this.licenseRequest = request.closed ? null : request;
    }

    private buildRequirementLinks(): void {
        this.requirementLinks.clear();
        this.assessmentLinks.clear();
        if (!this.state.isOpen) return;
        for (const item of this.visibleRows) {
            const source = item.kind === "task" ? item.task : item.evidence;
            const formKey = this.navigableLicenses.get(source.productLicenseId);
            if (!formKey) continue;
            const route = formKey === "roc_form" ? "/roc_form" : "/saq_form";
            const productLicenseId = encodeBase64RouteValue(String(source.productLicenseId));
            this.assessmentLinks.set(source.productLicenseId, this.router.createUrlTree(
                [route], { queryParams: { productLicenseId } }
            ));
            if (item.kind !== "task") continue;
            for (const requirement of item.task.requirements) {
                const sectionKey = requirement.navigationSectionKey;
                const requirementKey = requirement.navigationRequirementKey;
                // The shared route encoder returns its input for malformed Unicode.
                // Refuse those keys before they can become unencoded query values.
                try {
                    encodeURIComponent(sectionKey);
                    encodeURIComponent(requirementKey);
                } catch {
                    continue;
                }
                const activeSection = encodeBase64RouteValue(sectionKey);
                const activeRequirement = encodeBase64RouteValue(requirementKey);
                if (!/^[A-Za-z0-9+/]+={0,2}$/.test(activeSection) ||
                    !/^[A-Za-z0-9+/]+={0,2}$/.test(activeRequirement)) continue;
                const link = this.router.createUrlTree(
                    [route],
                    { queryParams: {
                        productLicenseId,
                        activeSection,
                        activeRequirement
                    } }
                );
                this.requirementLinks.set(this.requirementLinkKey(item.task, requirement), link);
            }
        }
    }

    private clearDeleteRequests(): void {
        for (const request of this.deleteRequests.values()) request.unsubscribe();
        this.deleteRequests.clear();
        this.deleteErrors.clear();
    }

    private deleteTask(task: AssessmentTaskSummary): void {
        const scope = this.scopeVersion;
        const token = this.login.getToken();
        this.deleteErrors.delete(task.id);
        const request = this.tasks.deleteTask(task.id, task.revision, task).subscribe({
            next: () => {
                if (scope !== this.scopeVersion || token !== this.login.getToken()) return;
                this.deleteRequests.delete(task.id);
                const formContext = this.formLaunch.getSnapshot().context;
                if (formContext?.kind === "edit" && formContext.taskId === task.id) {
                    this.formLaunch.close();
                }
            },
            error: (error: unknown) => {
                if (scope !== this.scopeVersion || token !== this.login.getToken() || !this.state.isOpen) return;
                this.deleteRequests.delete(task.id);
                const conflict = error instanceof HttpErrorResponse && error.error?.code === "TASK_CONFLICT";
                this.deleteErrors.set(task.id, {
                    conflict,
                    message: conflict
                        ? "This task changed. Reload the list before deleting it."
                        : "Unable to delete this task. Reload the list and try again."
                });
            }
        });
        if (!request.closed) this.deleteRequests.set(task.id, request);
    }

    private prepareOptionSearch<T extends number | string>(options: FilterOptionState<T>, value: string): void {
        options.request?.unsubscribe();
        options.request = null;
        options.version += 1;
        options.items = [];
        options.page = 0;
        options.total = 0;
        options.search = value;
        options.loading = true;
        options.error = null;
    }

    private loadAssignees(append = false): void {
        if (this.isCombinedHeader) {
            this.loadOptionPage(this.assigneeOptions,
                (page, search) => this.workItems.getAssigneeFilterOptions(
                    this.headerOptionFilters(page, search, "assignees"))
                    .pipe(map((result) => ({
                        items: result.items, page, pageSize: result.limit, total: result.total
                    }))),
                (item: AssessmentTaskAssigneeOption) => ({ value: item.userId, label: item.label }), append);
            return;
        }
        this.loadOptionPage(this.assigneeOptions,
            (page, search) => this.tasks.getAssigneeFilterOptions({ page, pageSize: OPTION_PAGE_SIZE, search }),
            (item: AssessmentTaskAssigneeOption) => ({ value: item.userId, label: item.label }), append);
    }

    private loadAssessments(append = false): void {
        if (this.isCombinedHeader) {
            this.loadOptionPage(this.assessmentOptions,
                (page, search) => this.workItems.getAssessmentFilterOptions(
                    this.headerOptionFilters(page, search, "assessments"))
                    .pipe(map((result) => ({
                        items: result.items, page, pageSize: result.limit, total: result.total
                    }))),
                (item: AssessmentTaskAssessmentOption) => ({
                    value: item.productLicenseId,
                    label: this.assessmentOptions.labels.get(item.productLicenseId) || item.label
                }), append);
            return;
        }
        this.loadOptionPage(this.assessmentOptions,
            (page, search) => this.tasks.getAssessmentFilterOptions({ page, pageSize: OPTION_PAGE_SIZE, search }),
            (item: AssessmentTaskAssessmentOption) => ({
                value: item.productLicenseId,
                label: this.assessmentOptions.labels.get(item.productLicenseId) || item.label
            }), append);
    }

    private loadStatuses(): void {
        if (this.isCombinedHeader) {
            this.loadOptionPage(this.statusOptions,
                (page, search) => this.workItems.getStatusFilterOptions(
                    this.headerOptionFilters(page, search, "statuses"))
                    .pipe(map((result) => ({
                        items: result.items, page, pageSize: result.limit, total: result.total
                    }))),
                (item) => ({ value: item.value, label: item.label }), false);
            return;
        }
        this.loadOptionPage(this.statusOptions,
            (page, search) => this.tasks.getStatusFilterOptions({ page, pageSize: OPTION_PAGE_SIZE, search }),
            (item: AssessmentTaskStatusOption) => ({ value: item.status, label: item.label }), false);
    }

    private headerOptionFilters(page: number, search: string,
        choice: "assignees" | "assessments" | "statuses"): AssessmentWorkItemOptionFilters {
        const filters = this.state.combinedHeader?.filters;
        if (!this.isCombinedHeader || !filters) throw new Error("A header context is required.");
        return {
            view: "header",
            itemType: filters.itemType,
            assignedToMe: filters.assignedToMe,
            assigneeUserIds: choice === "assignees" ? undefined : filters.assigneeUserIds,
            assessmentIds: choice === "assessments" ? undefined : filters.assessmentIds,
            statuses: choice === "statuses" ? undefined : filters.statuses,
            itemSearch: filters.search,
            search,
            offset: (page - 1) * OPTION_PAGE_SIZE,
            limit: OPTION_PAGE_SIZE
        };
    }

    private loadOptionPage<T extends number | string, D>(
        options: FilterOptionState<T>, fetch: (page: number, search: string) => Observable<AssessmentTaskPage<D>>,
        choice: (item: D) => FilterChoice<T>, append: boolean
    ): void {
        if (!this.state.isOpen || (append &&
            (options.loading || options.page * OPTION_PAGE_SIZE >= options.total))) return;
        options.request?.unsubscribe();
        options.request = null;
        const version = ++options.version;
        const scope = this.scopeVersion;
        const context = this.state.context;
        const token = this.login.getToken();
        const headerFilters = this.isCombinedHeader ? this.state.combinedHeader?.filters : null;
        const isCurrent = (): boolean => scope === this.scopeVersion && version === options.version &&
            this.state.isOpen && context === this.state.context && token === this.login.getToken() &&
            (headerFilters === null || headerFilters === this.state.combinedHeader?.filters);
        const pageNumber = append ? options.page + 1 : 1;
        if (!append) {
            options.items = [];
            options.page = 0;
            options.total = 0;
        }
        options.loading = true;
        options.error = null;
        let request: Observable<AssessmentTaskPage<D>>;
        try {
            request = fetch(pageNumber, options.search);
        } catch {
            options.loading = false;
            options.error = "Unable to load filter choices.";
            return;
        }
        const subscription = request.subscribe({
            next: (page) => {
                if (!isCurrent()) return;
                if (page.page !== pageNumber || page.pageSize !== OPTION_PAGE_SIZE) {
                    options.loading = false;
                    options.error = "Unable to load filter choices.";
                    options.request = null;
                    return;
                }
                const choices = page.items.map(choice);
                for (const item of choices) options.labels.set(item.value, item.label);
                options.items = append
                    ? [...new Map<T, FilterChoice<T>>(
                        [...options.items, ...choices].map((item): [T, FilterChoice<T>] => [item.value, item])
                    ).values()]
                    : choices;
                options.page = page.page;
                options.total = page.total;
                options.loading = false;
            },
            error: () => {
                if (!isCurrent()) return;
                options.loading = false;
                options.error = "Unable to load filter choices.";
                options.request = null;
            },
            complete: () => {
                if (isCurrent()) options.request = null;
            }
        });
        options.request = subscription.closed ? null : subscription;
    }
}
